import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import {
  MapPin, Calendar, Truck, Clock, Route, Sparkles, Plus, Eye, Trash2,
  CheckCircle2, FileText, ChevronRight, ChevronLeft, Package, Search,
  AlertTriangle, Zap, ToggleLeft, ToggleRight, CheckSquare, Square, List
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const STATUS_COLORS = {
  'Draft': 'bg-slate-100 text-slate-700 border-slate-300',
  'Published': 'bg-blue-100 text-blue-700 border-blue-300',
  'Completed': 'bg-green-100 text-green-700 border-green-300'
};

const SHIFT_LABELS = {
  'mañana': 'Mañana (07:00-14:00)',
  'tarde': 'Tarde (14:00-21:00)',
  'noche': 'Noche (23:00-06:00)'
};

// Volume estimate per item type (m³)
const VOL_MAP = {
  'sofá': 1.5, 'sofa': 1.5, 'sillón': 0.8, 'sillon': 0.8,
  'mesa': 0.8, 'silla': 0.2, 'armario': 2.5, 'ropero': 2.5,
  'cama': 2.0, 'colchón': 1.5, 'colchon': 1.5,
  'estantería': 0.8, 'estanteria': 0.8, 'escritorio': 0.7,
  'cómoda': 1.2, 'comoda': 1.2, 'mesita': 0.3,
  'nevera': 0.8, 'frigorífico': 0.8, 'frigorifico': 0.8,
  'lavadora': 0.5, 'secadora': 0.5, 'lavavajillas': 0.5,
  'microondas': 0.1, 'horno': 0.2, 'televisión': 0.3, 'television': 0.3,
  'aire': 0.3, 'bicicleta': 0.6, 'cochecito': 0.6,
};

function estimateVolume(sol) {
  if (sol.ai_volumen && sol.ai_volumen > 0) return sol.ai_volumen;
  const lista = sol.enseres_lista || [];
  if (lista.length === 0) {
    const t = (sol.tipo_enseres || '').toLowerCase();
    if (t.includes('electro') || t.includes('nevera') || t.includes('lava')) return 0.6;
    return 1.0;
  }
  let total = 0;
  for (const item of lista) {
    const lower = item.toLowerCase();
    const qtyMatch = lower.match(/\(x(\d+)\)/);
    const qty = qtyMatch ? parseInt(qtyMatch[1]) : 1;
    let vol = 0.8; // default
    for (const [key, v] of Object.entries(VOL_MAP)) {
      if (lower.includes(key)) { vol = v; break; }
    }
    total += vol * qty;
  }
  return Math.round(total * 10) / 10 || 1.0;
}

// Capacity bar component
function CapacityBar({ used, capacity, overridden }) {
  const pct = capacity > 0 ? Math.min((used / capacity) * 100, 100) : 0;
  const over = used > capacity;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className={over && !overridden ? 'text-red-600 font-medium' : 'text-slate-600'}>
          {over && !overridden ? '⚠️ ' : ''}Carga: <b>{used.toFixed(1)}</b> / {capacity} m³
        </span>
        <span className={over ? 'text-amber-600 font-medium' : 'text-slate-400'}>
          {pct.toFixed(0)}%{over ? ' (EXCEDE)' : ''}
        </span>
      </div>
      <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            over ? 'bg-amber-400' : pct > 80 ? 'bg-orange-400' : 'bg-[#009940]'
          }`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

export default function AdminRutas() {
  const [routes, setRoutes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date: '', status: '', zona: '' });

  // Optimize modal state
  const [optimizeModalOpen, setOptimizeModalOpen] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeForm, setOptimizeForm] = useState({
    date: new Date().toISOString().split('T')[0],
    shift: 'mañana',
    shift_start: '07:00',
    shift_end: '14:00',
    service_time_per_stop: 5,
    zona: ''
  });
  const [optimizedRoutes, setOptimizedRoutes] = useState(null);

  // Manual route modal state — 2-step
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualStep, setManualStep] = useState(1); // 1 = metadata, 2 = pick solicitudes
  const [manualForm, setManualForm] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    shift: 'mañana',
    vehicle_id: '',
    driver_name: '',
    zona: ''
  });
  const [vehicles, setVehicles] = useState([]);

  // Step 2: solicitude picker state
  const [pendingList, setPendingList] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [capacityOverride, setCapacityOverride] = useState(false);

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === manualForm.vehicle_id),
    [vehicles, manualForm.vehicle_id]
  );
  const vehicleCapacity = selectedVehicle?.capacidad_m3 || 20;

  const selectedSols = useMemo(
    () => pendingList.filter((s) => selectedIds.has(s.id)),
    [pendingList, selectedIds]
  );
  const usedVolume = useMemo(
    () => selectedSols.reduce((acc, s) => acc + estimateVolume(s), 0),
    [selectedSols]
  );

  const filteredPending = useMemo(() => {
    if (!pickerSearch.trim()) return pendingList;
    const q = pickerSearch.toLowerCase();
    return pendingList.filter(
      (s) =>
        (s.ticket_code || '').toLowerCase().includes(q) ||
        (s.nombre_completo || '').toLowerCase().includes(q) ||
        (s.zona || '').toLowerCase().includes(q) ||
        (s.direccion_aprox || '').toLowerCase().includes(q) ||
        (s.tipo_enseres || '').toLowerCase().includes(q)
    );
  }, [pendingList, pickerSearch]);

  // ── Quick single-route state ─────────────────────────────────────────────
  const [quickModalOpen, setQuickModalOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({
    name: `Ruta Única - ${new Date().toISOString().split('T')[0]}`,
    date: new Date().toISOString().split('T')[0],
    shift: 'mañana',
    vehicle_id: '',
    driver_name: '',
    zona: '',
  });
  const [quickPending, setQuickPending] = useState([]);
  const [quickLoading, setQuickLoading] = useState(false);
  const [creatingQuick, setCreatingQuick] = useState(false);

  const fetchQuickPending = async (date = '', zona = '') => {
    if (!date) {
      setQuickPending([]);
      return;
    }
    setQuickLoading(true);
    try {
      const params = { estado: 'Notificada', per_page: 500, fecha_preferida: date };
      if (zona) params.zona = zona;
      const resp = await axios.get(`${API}/admin/solicitudes`, { headers: authHeaders(), params });
      setQuickPending(resp.data.solicitudes || []);
    } catch {
      toast.error('Error al cargar solicitudes pendientes');
    } finally {
      setQuickLoading(false);
    }
  };

  const handleCreateQuickRoute = async () => {
    if (!quickForm.name.trim() || !quickForm.vehicle_id) {
      toast.error('Nombre de ruta y vehículo son obligatorios');
      return;
    }
    if (!quickForm.date) {
      toast.error('Selecciona una fecha de recogida');
      return;
    }
    if (quickPending.length === 0) {
      toast.error('No hay solicitudes pendientes para asignar');
      return;
    }
    // Safety check: all solicitudes must match the selected date
    const wrongDate = quickPending.filter(s => !s.fecha_preferida?.startsWith(quickForm.date));
    if (wrongDate.length > 0) {
      toast.error(`Hay ${wrongDate.length} solicitudes de otra fecha. Recarga la lista antes de continuar.`);
      return;
    }
    setCreatingQuick(true);
    try {
      const allIds = quickPending.map((s) => s.id);
      await axios.post(
        `${API}/admin/routes/manual`,
        { ...quickForm, ticket_ids: allIds },
        { headers: authHeaders() }
      );
      toast.success(`Ruta única creada con ${allIds.length} solicitudes asignadas`);
      setQuickModalOpen(false);
      fetchRoutes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear la ruta única');
    } finally {
      setCreatingQuick(false);
    }
  };

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const params = { per_page: 50 };
      if (filters.date) params.date = filters.date;
      if (filters.status) params.status = filters.status;
      if (filters.zona) params.zona = filters.zona;
      const resp = await axios.get(`${API}/admin/routes`, { headers: authHeaders(), params });
      setRoutes(resp.data.routes);
      setTotal(resp.data.total);
    } catch {
      toast.error('Error al cargar rutas');
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const resp = await axios.get(`${API}/admin/vehicles`, {
        headers: authHeaders(),
        params: { per_page: 200, operativa: true }
      });
      setVehicles(resp.data.vehicles);
    } catch (err) {
      console.error('Error loading vehicles', err);
    }
  };

  const fetchPendingSolicitudes = async (date, zona) => {
    if (!date) {
      setPendingList([]);
      return;
    }
    setPendingLoading(true);
    try {
      const params = { estado: 'Notificada', per_page: 200, fecha_preferida: date };
      if (zona) params.zona = zona;
      const resp = await axios.get(`${API}/admin/solicitudes`, { headers: authHeaders(), params });
      setPendingList(resp.data.solicitudes || []);
    } catch {
      toast.error('Error al cargar solicitudes pendientes');
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => { fetchRoutes(); }, []);
  useEffect(() => { fetchVehicles(); }, []);

  // ── Auto-fill: add solicitudes until capacity ──────────────────────────────
  const handleAutoFill = () => {
    const sorted = [...pendingList].sort((a, b) => estimateVolume(b) - estimateVolume(a));
    const newSel = new Set(selectedIds);
    let vol = usedVolume;
    for (const s of sorted) {
      if (newSel.has(s.id)) continue;
      const sv = estimateVolume(s);
      if (vol + sv <= vehicleCapacity) {
        newSel.add(s.id);
        vol += sv;
      }
    }
    setSelectedIds(newSel);
    toast.success(`Camión llenado automáticamente (${vol.toFixed(1)} / ${vehicleCapacity} m³)`);
  };

  const toggleSol = (id, solVol) => {
    const newSel = new Set(selectedIds);
    if (newSel.has(id)) {
      newSel.delete(id);
    } else {
      const newVol = usedVolume + solVol;
      if (!capacityOverride && newVol > vehicleCapacity) {
        toast.warning(
          `Excede la capacidad del vehículo (${newVol.toFixed(1)} > ${vehicleCapacity} m³). Activa "Forzar carga" para añadirla.`
        );
        return;
      }
      newSel.add(id);
    }
    setSelectedIds(newSel);
  };

  const goToStep2 = async () => {
    if (!manualForm.name.trim() || !manualForm.vehicle_id) {
      toast.error('Nombre de ruta y vehículo son obligatorios');
      return;
    }
    setPickerSearch('');
    setSelectedIds(new Set());
    setCapacityOverride(false);
    await fetchPendingSolicitudes(manualForm.date, manualForm.zona);
    setManualStep(2);
  };

  const handleCreateManual = async () => {
    try {
      await axios.post(
        `${API}/admin/routes/manual`,
        { ...manualForm, ticket_ids: Array.from(selectedIds) },
        { headers: authHeaders() }
      );
      const n = selectedIds.size;
      toast.success(`Ruta creada con ${n} solicitud${n !== 1 ? 'es' : ''} asignada${n !== 1 ? 's' : ''}`);
      setManualModalOpen(false);
      resetManualModal();
      fetchRoutes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear ruta manual');
    }
  };

  const resetManualModal = () => {
    setManualStep(1);
    setManualForm({
      name: '',
      date: new Date().toISOString().split('T')[0],
      shift: 'mañana',
      vehicle_id: '',
      driver_name: '',
      zona: ''
    });
    setPendingList([]);
    setSelectedIds(new Set());
    setPickerSearch('');
    setCapacityOverride(false);
  };

  const handleOptimize = async () => {
    setOptimizing(true);
    try {
      const resp = await axios.post(`${API}/admin/routes/optimize`, optimizeForm, { headers: authHeaders() });
      setOptimizedRoutes(resp.data);
      toast.success(`${resp.data.routes.length} rutas optimizadas generadas`);
    } catch {
      toast.error('Error al optimizar rutas');
    } finally {
      setOptimizing(false);
    }
  };

  const handleConfirmOptimizedRoutes = async () => {
    if (!optimizedRoutes || !optimizedRoutes.routes.length) return;
    try {
      await axios.post(`${API}/admin/routes/optimize/confirm`, optimizedRoutes.routes, { headers: authHeaders() });
      toast.success(`${optimizedRoutes.routes.length} rutas guardadas correctamente`);
      setOptimizeModalOpen(false);
      setOptimizedRoutes(null);
      fetchRoutes();
    } catch {
      toast.error('Error al guardar rutas');
    }
  };

  const handleDeleteRoute = async (route) => {
    if (!window.confirm(`¿Eliminar ruta ${route.name}? Las solicitudes asignadas se desasignarán.`)) return;
    try {
      await axios.delete(`${API}/admin/routes/${route.id}`, { headers: authHeaders() });
      toast.success('Ruta eliminada');
      fetchRoutes();
    } catch {
      toast.error('Error al eliminar ruta');
    }
  };

  const handlePublishRoute = async (route) => {
    try {
      await axios.patch(`${API}/admin/routes/${route.id}`, { status: 'Published' }, { headers: authHeaders() });
      toast.success('Ruta publicada y solicitudes asignadas');
      fetchRoutes();
    } catch {
      toast.error('Error al publicar ruta');
    }
  };

  const handleDownloadRoutePdf = async (route) => {
    try {
      const resp = await axios.get(`${API}/admin/routes/${route.id}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Ruta_${(route.name || 'ruta').replace(/[^a-zA-Z0-9_-]/g, '_')}_${route.date || ''}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch {
      toast.error('No se pudo descargar el PDF');
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-semibold text-[#374B67]">Gestión de Rutas</h1>
            <p className="text-sm text-slate-500">{total} rutas total</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                setQuickForm(f => ({ ...f, name: `Ruta Única - ${today}`, date: today, vehicle_id: '', driver_name: '', zona: '' }));
                setQuickPending([]);
                setQuickModalOpen(true);
                fetchQuickPending(today, '');
              }}
              className="bg-[#374B67] hover:bg-[#2A3B52] text-white"
              data-testid="routes-create-quick-button"
            >
              <List size={16} className="mr-2" />
              Ruta Única
            </Button>
            <Button
              onClick={() => { resetManualModal(); setManualModalOpen(true); }}
              variant="outline"
              data-testid="routes-create-manual-button"
            >
              <Plus size={16} className="mr-2" />
              Ruta Manual
            </Button>
            <Button
              onClick={() => setOptimizeModalOpen(true)}
              className="bg-[#009940] hover:bg-[#007F35]"
              data-testid="routes-optimize-button"
            >
              <Sparkles size={16} className="mr-2" />
              Optimizar con IA
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-600 mb-1">Fecha</Label>
              <Input type="date" value={filters.date}
                onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                data-testid="routes-filter-date" />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1">Estado</Label>
              <Select value={filters.status} onValueChange={(val) => setFilters({ ...filters, status: val })}>
                <SelectTrigger data-testid="routes-filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Todos</SelectItem>
                  <SelectItem value="Draft">Borrador</SelectItem>
                  <SelectItem value="Published">Publicada</SelectItem>
                  <SelectItem value="Completed">Completada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1">Zona</Label>
              <Input placeholder="Filtrar por zona..." value={filters.zona}
                onChange={(e) => setFilters({ ...filters, zona: e.target.value })}
                data-testid="routes-filter-zona" />
            </div>
          </div>
          <Button onClick={fetchRoutes} data-testid="routes-apply-filters-button">Aplicar Filtros</Button>
        </div>

        {/* Routes List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando rutas...</div>
        ) : routes.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Route size={48} className="mx-auto mb-3 text-slate-300" />
            <p>No hay rutas creadas aún</p>
            <p className="text-xs text-slate-400 mt-1">Usa "Optimizar con IA" o "Ruta Manual"</p>
          </div>
        ) : (
          <div className="space-y-3">
            {routes.map((route) => (
              <div key={route.id}
                className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                data-testid={`route-card-${route.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-slate-900">{route.name}</h3>
                      <Badge className={STATUS_COLORS[route.status] || STATUS_COLORS.Draft}>
                        {route.status === 'Draft' ? 'Borrador' : route.status === 'Published' ? 'Publicada' : 'Completada'}
                      </Badge>
                      {route.type === 'IA' && (
                        <Badge variant="secondary" className="text-xs"><Sparkles size={10} className="mr-1" />IA</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-slate-600">
                      <div className="flex items-center gap-1"><Calendar size={14} /><span>{route.date}</span></div>
                      <div className="flex items-center gap-1"><Clock size={14} /><span>{SHIFT_LABELS[route.shift] || route.shift}</span></div>
                      <div className="flex items-center gap-1"><Truck size={14} /><span>{route.vehicle_matricula}</span></div>
                      <div className="flex items-center gap-1"><MapPin size={14} /><span>{route.metrics?.stops || 0} paradas</span></div>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      {route.metrics?.distance_km && <span className="mr-3">📏 {route.metrics.distance_km} km</span>}
                      {route.metrics?.time_minutes && <span className="mr-3">⏱️ {route.metrics.time_minutes} min</span>}
                      {route.cluster_volume_m3 != null && route.vehicle_capacity_m3 && (
                        <span>📦 {route.cluster_volume_m3} / {route.vehicle_capacity_m3} m³</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Link to={`/admin/rutas/${route.id}`}
                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      title="Ver detalle" data-testid={`route-view-${route.id}`}>
                      <Eye size={16} className="text-slate-600" />
                    </Link>
                    <button onClick={() => handleDownloadRoutePdf(route)}
                      className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      title="Descargar PDF con QR" data-testid={`route-pdf-${route.id}`}>
                      <FileText size={16} className="text-[#374B67]" />
                    </button>
                    {route.status === 'Draft' && (
                      <button onClick={() => handlePublishRoute(route)}
                        className="p-2 rounded-lg hover:bg-green-50 text-green-600 transition-colors"
                        title="Publicar ruta" data-testid={`route-publish-${route.id}`}>
                        <CheckCircle2 size={16} />
                      </button>
                    )}
                    <button onClick={() => handleDeleteRoute(route)}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                      title="Eliminar" data-testid={`route-delete-${route.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Manual Route Modal (2 steps) ─────────────────────────────────── */}
      <Dialog open={manualModalOpen} onOpenChange={(o) => { if (!o) resetManualModal(); setManualModalOpen(o); }}>
        <DialogContent
          className={manualStep === 2 ? 'sm:max-w-2xl max-h-[90vh] flex flex-col' : 'sm:max-w-md'}
          data-testid="manual-route-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {manualStep === 1 ? <><Plus size={18} /> Crear Ruta Manual</> : <><Package size={18} /> Asignar Solicitudes</>}
            </DialogTitle>
            <DialogDescription>
              {manualStep === 1
                ? 'Paso 1 de 2 — Define los datos de la ruta y el vehículo.'
                : `Paso 2 de 2 — Selecciona las solicitudes para la ruta "${manualForm.name}".`}
            </DialogDescription>
            {/* Step indicator */}
            <div className="flex items-center gap-1 mt-2">
              {[1, 2].map((s) => (
                <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${manualStep >= s ? 'bg-[#374B67]' : 'bg-slate-200'}`} />
              ))}
            </div>
          </DialogHeader>

          {/* ── STEP 1: Route metadata ─────────────────────────────────── */}
          {manualStep === 1 && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-xs">Nombre de la ruta *</Label>
                <Input value={manualForm.name}
                  onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                  placeholder="Ej: Ruta Centro - Mañana"
                  data-testid="manual-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Fecha *</Label>
                  <Input type="date" value={manualForm.date}
                    onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
                    data-testid="manual-date-input" />
                </div>
                <div>
                  <Label className="text-xs">Turno</Label>
                  <Select value={manualForm.shift} onValueChange={(val) => setManualForm({ ...manualForm, shift: val })}>
                    <SelectTrigger data-testid="manual-shift-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mañana">Mañana</SelectItem>
                      <SelectItem value="tarde">Tarde</SelectItem>
                      <SelectItem value="noche">Noche</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Vehículo *</Label>
                <Select value={manualForm.vehicle_id} onValueChange={(val) => setManualForm({ ...manualForm, vehicle_id: val })}>
                  <SelectTrigger data-testid="manual-vehicle-select"><SelectValue placeholder="Seleccionar vehículo" /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.matricula} — {v.marca || ''} {v.modelo || ''} {v.capacidad_m3 ? `(${v.capacidad_m3} m³)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedVehicle?.capacidad_m3 && (
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <Truck size={11} /> Capacidad: <b>{selectedVehicle.capacidad_m3} m³</b>
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs">Conductor (opcional)</Label>
                <Input value={manualForm.driver_name}
                  onChange={(e) => setManualForm({ ...manualForm, driver_name: e.target.value })}
                  placeholder="Nombre del conductor" data-testid="manual-driver-input" />
              </div>
              <div>
                <Label className="text-xs">Filtrar zona (opcional)</Label>
                <Input value={manualForm.zona}
                  onChange={(e) => setManualForm({ ...manualForm, zona: e.target.value })}
                  placeholder="Ej: Majadahonda Centro — filtrará solicitudes de esa zona"
                  data-testid="manual-zona-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { resetManualModal(); setManualModalOpen(false); }}
                  data-testid="manual-cancel-button">Cancelar</Button>
                <Button onClick={goToStep2}
                  disabled={!manualForm.name.trim() || !manualForm.vehicle_id}
                  className="bg-[#374B67] hover:bg-[#2A3B52]" data-testid="manual-next-button">
                  Seleccionar solicitudes <ChevronRight size={16} className="ml-1" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* ── STEP 2: Solicitude picker ──────────────────────────────── */}
          {manualStep === 2 && (
            <div className="flex flex-col flex-1 overflow-hidden py-2">

              {/* Capacity bar + controls */}
              <div className="space-y-3 mb-4 pb-3 border-b border-slate-100">
                <CapacityBar used={usedVolume} capacity={vehicleCapacity} overridden={capacityOverride} />

                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-600">
                    <b>{selectedIds.size}</b> seleccionadas de {pendingList.length} pendientes
                  </span>
                  <div className="flex items-center gap-2">
                    {/* Force capacity override toggle */}
                    <button
                      onClick={() => setCapacityOverride(!capacityOverride)}
                      className={`flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 border transition-colors ${
                        capacityOverride
                          ? 'bg-amber-50 border-amber-300 text-amber-700'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                      title="Permite añadir solicitudes aunque se supere la capacidad del vehículo"
                      data-testid="manual-capacity-override-toggle"
                    >
                      {capacityOverride ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                      Forzar carga
                    </button>
                    {/* Auto-fill button */}
                    <Button size="sm" variant="outline"
                      onClick={handleAutoFill}
                      disabled={pendingList.length === 0}
                      className="text-xs"
                      data-testid="manual-autofill-button"
                    >
                      <Zap size={13} className="mr-1 text-amber-500" />
                      Llenar auto
                    </Button>
                  </div>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por código, nombre, zona, dirección..."
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    className="pl-8 text-sm"
                    data-testid="manual-picker-search"
                  />
                </div>
              </div>

              {/* Solicitude list */}
              {pendingLoading ? (
                <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
                  Cargando solicitudes pendientes...
                </div>
              ) : filteredPending.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm gap-2">
                  <Package size={36} className="text-slate-300" />
                  <p>No hay solicitudes pendientes para esta fecha{manualForm.zona ? ` en "${manualForm.zona}"` : ''}.</p>
                  <p className="text-xs">Puedes crear la ruta vacía y añadir paradas desde el detalle.</p>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="manual-picker-list">
                  {filteredPending.map((sol) => {
                    const vol = estimateVolume(sol);
                    const checked = selectedIds.has(sol.id);
                    const wouldExceed = !checked && !capacityOverride && (usedVolume + vol) > vehicleCapacity;
                    return (
                      <label
                        key={sol.id}
                        htmlFor={`sol-${sol.id}`}
                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                          checked
                            ? 'border-[#374B67] bg-[#374B67]/5'
                            : wouldExceed
                            ? 'border-slate-200 bg-slate-50 opacity-50'
                            : 'border-slate-200 bg-white hover:border-[#374B67]/40 hover:bg-slate-50'
                        }`}
                        data-testid={`sol-picker-${sol.id}`}
                      >
                        <input
                          id={`sol-${sol.id}`}
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSol(sol.id, vol)}
                          disabled={wouldExceed}
                          className="mt-1 accent-[#374B67]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-mono font-semibold text-[#374B67]">{sol.ticket_code}</span>
                            <span className="text-xs text-slate-500">{sol.zona}</span>
                            <Badge variant="outline" className="text-xs py-0 h-4">{sol.tipo_enseres}</Badge>
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5 truncate">{sol.direccion_aprox || sol.direccion_completa}</p>
                          {sol.enseres_lista?.length > 0 && (
                            <p className="text-xs text-slate-400 truncate">{sol.enseres_lista.join(', ')}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-400">
                              📦 ~{vol} m³ estimados
                              {sol.ai_volumen ? ' (IA)' : ' (estimado)'}
                            </span>
                            {wouldExceed && (
                              <span className="text-xs text-amber-600 flex items-center gap-0.5">
                                <AlertTriangle size={10} /> Excedería capacidad
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Footer */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between mt-3">
                <Button variant="outline" onClick={() => setManualStep(1)} data-testid="manual-back-button">
                  <ChevronLeft size={16} className="mr-1" /> Volver
                </Button>
                <Button
                  onClick={handleCreateManual}
                  className="bg-[#009940] hover:bg-[#007F35]"
                  data-testid="manual-create-button"
                >
                  Crear ruta con {selectedIds.size} parada{selectedIds.size !== 1 ? 's' : ''}
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Optimize Modal ────────────────────────────────────────────────── */}
      <Dialog open={optimizeModalOpen} onOpenChange={setOptimizeModalOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="optimize-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={20} className="text-[#009940]" />Optimizar Rutas con IA
            </DialogTitle>
            <DialogDescription>
              La IA generará rutas optimizadas considerando proximidad, capacidad vehicular y volumen estimado
            </DialogDescription>
          </DialogHeader>

          {!optimizedRoutes ? (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Fecha</Label>
                  <Input type="date" value={optimizeForm.date}
                    onChange={(e) => setOptimizeForm({ ...optimizeForm, date: e.target.value })}
                    data-testid="optimize-date-input" />
                </div>
                <div>
                  <Label className="text-xs">Turno</Label>
                  <Select value={optimizeForm.shift}
                    onValueChange={(val) => {
                      const shifts = { 'mañana': ['07:00', '14:00'], 'tarde': ['14:00', '21:00'], 'noche': ['23:00', '06:00'] };
                      setOptimizeForm({ ...optimizeForm, shift: val, shift_start: shifts[val][0], shift_end: shifts[val][1] });
                    }}>
                    <SelectTrigger data-testid="optimize-shift-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mañana">Mañana (07:00-14:00)</SelectItem>
                      <SelectItem value="tarde">Tarde (14:00-21:00)</SelectItem>
                      <SelectItem value="noche">Noche (23:00-06:00)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Hora inicio</Label>
                  <Input type="time" value={optimizeForm.shift_start}
                    onChange={(e) => setOptimizeForm({ ...optimizeForm, shift_start: e.target.value })}
                    data-testid="optimize-start-time-input" />
                </div>
                <div>
                  <Label className="text-xs">Hora fin</Label>
                  <Input type="time" value={optimizeForm.shift_end}
                    onChange={(e) => setOptimizeForm({ ...optimizeForm, shift_end: e.target.value })}
                    data-testid="optimize-end-time-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Zona (opcional)</Label>
                <Input placeholder="Dejar vacío para todas las zonas" value={optimizeForm.zona}
                  onChange={(e) => setOptimizeForm({ ...optimizeForm, zona: e.target.value })}
                  data-testid="optimize-zona-input" />
              </div>
              <div>
                <Label className="text-xs">Tiempo por parada (minutos)</Label>
                <Input type="number" min="1" max="30" value={optimizeForm.service_time_per_stop}
                  onChange={(e) => setOptimizeForm({ ...optimizeForm, service_time_per_stop: parseInt(e.target.value) })}
                  data-testid="optimize-service-time-input" />
              </div>
              <Button onClick={handleOptimize} disabled={optimizing}
                className="w-full bg-[#009940] hover:bg-[#007F35]" data-testid="optimize-generate-button">
                {optimizing ? 'Optimizando...' : 'Generar Rutas Optimizadas'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                <p className="font-semibold text-slate-900">Resumen de optimización:</p>
                <ul className="space-y-1 text-slate-600">
                  <li>✅ Solicitudes procesadas: <b>{optimizedRoutes.summary.total_requests}</b></li>
                  <li>🚛 Rutas generadas: <b>{optimizedRoutes.summary.total_routes}</b></li>
                  <li>📏 Distancia total: <b>{optimizedRoutes.summary.total_distance_km} km</b></li>
                  <li>⏱️ Tiempo total: <b>{optimizedRoutes.summary.total_time_minutes} min</b></li>
                  <li>📍 Paradas promedio: <b>{optimizedRoutes.summary.avg_stops_per_route}</b></li>
                </ul>
              </div>
              {optimizedRoutes.ai_suggestions && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-sm">
                  <p className="font-semibold text-teal-900 mb-2 flex items-center gap-2"><Sparkles size={14} />Sugerencias de IA:</p>
                  <p className="text-teal-800 whitespace-pre-wrap">{optimizedRoutes.ai_suggestions}</p>
                </div>
              )}
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-600">Rutas a guardar:</p>
                {optimizedRoutes.routes.map((route, idx) => (
                  <div key={idx} className="bg-white border border-slate-200 rounded-lg p-3 text-sm">
                    <p className="font-semibold text-slate-900">{route.name}</p>
                    <p className="text-xs text-slate-600">
                      {route.vehicle_matricula} • {route.metrics.stops} paradas • {route.metrics.distance_km} km • {route.cluster_volume_m3}/{route.vehicle_capacity_m3} m³
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOptimizedRoutes(null)} className="flex-1" data-testid="optimize-back-button">
                  Volver a configurar
                </Button>
                <Button onClick={handleConfirmOptimizedRoutes} className="flex-1 bg-[#009940] hover:bg-[#007F35]" data-testid="optimize-confirm-button">
                  Guardar Rutas
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* ── Quick Single Route Modal ─────────────────────────────────────── */}
      <Dialog open={quickModalOpen} onOpenChange={setQuickModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="quick-route-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List size={18} className="text-[#374B67]" /> Crear Ruta Única
            </DialogTitle>
            <DialogDescription>
              Asigna todas las solicitudes con la <b>fecha preferida</b> seleccionada a una sola ruta, sin límite de capacidad.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Pending counter banner */}
            <div className={`rounded-xl px-4 py-3 text-sm font-medium flex items-center gap-3 ${
              quickLoading
                ? 'bg-slate-50 text-slate-500 border border-slate-200'
                : quickPending.length === 0
                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                : 'bg-[#374B67]/5 text-[#374B67] border border-[#374B67]/20'
            }`}>
              <Package size={18} className="shrink-0" />
              {quickLoading
                ? 'Buscando solicitudes para esa fecha...'
                : quickPending.length === 0
                ? `No hay solicitudes con fecha preferida ${quickForm.date}`
                : <><b>{quickPending.length}</b>&nbsp;solicitudes con fecha preferida <b>{quickForm.date}</b></>
              }
            </div>

            <div>
              <Label className="text-xs">Nombre de la ruta *</Label>
              <Input
                value={quickForm.name}
                onChange={(e) => setQuickForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ej: Ruta Única - 2026-06-03"
                data-testid="quick-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fecha de recogida *</Label>
                <Input
                  type="date"
                  value={quickForm.date}
                  onChange={(e) => {
                    const date = e.target.value;
                    setQuickForm(f => ({ ...f, date, name: `Ruta Única - ${date}` }));
                    fetchQuickPending(date, quickForm.zona);
                  }}
                  data-testid="quick-date-input"
                />
              </div>
              <div>
                <Label className="text-xs">Turno</Label>
                <Select value={quickForm.shift} onValueChange={(val) => setQuickForm(f => ({ ...f, shift: val }))}>
                  <SelectTrigger data-testid="quick-shift-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mañana">Mañana</SelectItem>
                    <SelectItem value="tarde">Tarde</SelectItem>
                    <SelectItem value="noche">Noche</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Vehículo *</Label>
              <Select value={quickForm.vehicle_id} onValueChange={(val) => setQuickForm(f => ({ ...f, vehicle_id: val }))}>
                <SelectTrigger data-testid="quick-vehicle-select"><SelectValue placeholder="Seleccionar vehículo" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.matricula} — {v.marca || ''} {v.modelo || ''} {v.capacidad_m3 ? `(${v.capacidad_m3} m³)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Conductor (opcional)</Label>
              <Input
                value={quickForm.driver_name}
                onChange={(e) => setQuickForm(f => ({ ...f, driver_name: e.target.value }))}
                placeholder="Nombre del conductor"
                data-testid="quick-driver-input"
              />
            </div>

            <div>
              <Label className="text-xs">Filtrar por zona (opcional)</Label>
              <Input
                value={quickForm.zona}
                onChange={(e) => {
                  const zona = e.target.value;
                  setQuickForm(f => ({ ...f, zona }));
                  fetchQuickPending(quickForm.date, zona);
                }}
                placeholder="Dejar vacío para todas las zonas"
                data-testid="quick-zona-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickModalOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleCreateQuickRoute}
              disabled={creatingQuick || quickLoading || quickPending.length === 0 || !quickForm.vehicle_id}
              className="bg-[#374B67] hover:bg-[#2A3B52]"
              data-testid="quick-create-button"
            >
              {creatingQuick
                ? 'Creando...'
                : `Crear ruta con ${quickPending.length} paradas`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </AdminLayout>
  );
}
